import React from "react";
import { useGlobal } from "utils/globalState";

import { Card } from "@jsainsburyplc/id-ui-common";
import { Section } from "@sainsburys-tech/luna-section";

import PageFormat from "components/PageFormat";

const FindOutMorePage = () => {
  const [readable_mission_name] = useGlobal("readable_mission_name");

  return (
    <PageFormat>
      <div className="c-hero">
        <h1 className="ln-u-h2">How to keep your account safe</h1>
      </div>

      <Card>
        <Section>
          <p>
            At {readable_mission_name}, we take online security seriously.
            That’s why we’ve added features to help keep your account safe.
          </p>

          <ul
            style={{
              listStyle: "initial",
              margin: "0",
              listStyleType: "none",
            }}
          >
            <li>
              <a
                className="ln-c-link ln-c-link--bare ln-c-link--cta ln-c-link--alpha"
                href="#fingerprint"
              >
                Use your fingerprint to log in
              </a>
            </li>
            <li>
              <a
                className="ln-c-link ln-c-link--bare ln-c-link--cta ln-c-link--alpha"
                href="#facial-recognition"
              >
                Use facial recognition to log in
              </a>
            </li>
            <li>
              <a
                className="ln-c-link ln-c-link--bare ln-c-link--cta ln-c-link--alpha"
                href="#verify-details"
              >
                Verify your details
              </a>
            </li>
            <li>
              <a
                className="ln-c-link ln-c-link--bare ln-c-link--cta ln-c-link--alpha"
                href="#secure-password"
              >
                Choose a secure password
              </a>
            </li>
          </ul>
        </Section>

        <Section id="fingerprint">
          <h2 className="ln-u-h4 ln-u-margin-bottom*1/2">
            Use your fingerprint to log in
          </h2>
          <p>
            If your phone has a fingerprint sensor, you can use it to log in to{" "}
            {readable_mission_name}. This gives you a secure and convenient way
            to access your account at all times without using your password.
          </p>
          <p>
            While using your fingerprint is a secure way to keep your account
            safe, it’s important to remember:
          </p>
          <div className="ln-u-margin-bottom*2">
            <ul style={{ listStyle: "initial", marginBottom: "0" }}>
              <li>
                <p>
                  You should still set up a strong password as well as your
                  fingerprint, as you may need to use it sometimes if your
                  fingerprint isn’t recognised.
                </p>
              </li>
              <li>
                <p>
                  A copy of your fingerprint could be used to access your{" "}
                  {readable_mission_name} account.
                </p>
              </li>
            </ul>
          </div>
          <h3 className="ln-u-h6 ln-u-margin-bottom*1/2">
            What about my fingerprint data?
          </h3>
          <p>
            Your fingerprint data is stored securely and never leaves your
            phone. Your data is not shared with {readable_mission_name} or any
            other apps on your phone.{" "}
          </p>
        </Section>

        <Section id="facial-recognition">
          <h2 className="ln-u-h4 ln-u-margin-bottom*1/2">
            Use facial recognition to log in
          </h2>
          <p>
            If your phone has facial recognition, you can use it to log in to{" "}
            {readable_mission_name}. This gives you a secure and convenient way
            to access your account at all times without using your password
          </p>
          <p>
            While using facial recognition is a secure way to keep your account
            safe, it’s important to remember:
          </p>
          <div className="ln-u-margin-bottom*2">
            <ul style={{ listStyle: "initial", marginBottom: "0" }}>
              <li>
                <p>
                  You should still set up a strong password as well as facial
                  recognition, as you may need to use it sometimes if your face
                  isn’t recognised.
                </p>
              </li>
              <li>
                <p>
                  Your {readable_mission_name} account could be accessed by
                  someone else if they held your phone up to your face, so keep
                  your phone in a safe place.
                </p>
              </li>
            </ul>
          </div>
          <h3 className="ln-u-h6 ln-u-margin-bottom*1/2">
            What about my facial recognition data?
          </h3>
          <p>
            Your facial recognition data is never stored by{" "}
            {readable_mission_name}. Your data is not shared with{" "}
            {readable_mission_name} or any other apps on your phone.
          </p>
        </Section>

        <Section id="verify-details">
          <h2 className="ln-u-h4 ln-u-margin-bottom*1/2">
            Verify your details
          </h2>
          <p>
            To protect your account from fraudulent activity, we’ll sometimes
            ask you to verify your details when you log in with a new device or
            we suspect your account has been compromised.
          </p>
          <p>
            We verify your details by sending a short verification code to the
            mobile number or email address attached to your{" "}
            {readable_mission_name} account. Just enter the code we’ve sent you
            to access your account.
          </p>
          <p>
            It’s important that you never share your verification code with
            anyone else.
          </p>
        </Section>

        <Section id="secure-password">
          <h2 className="ln-u-h4 ln-u-margin-bottom*1/2">
            Choose a secure password
          </h2>
          <p>
            Choosing a secure password is another way to keep your account safe.
            If your password is too simple or obvious, it could be easy to guess
            and your account security will be at risk.
          </p>
          <h3 className="ln-u-h6 ln-u-margin-bottom*1/2">Passwords to avoid</h3>
          <p>
            We currently prevent you from using your name, email address, or any
            previously-used passwords as your password. We also recommend
            avoiding any of the following as your password:
          </p>
          <div className="ln-u-margin-bottom*2">
            <ul style={{ listStyle: "initial", marginBottom: "0" }}>
              <li>the names of your family members or pets</li>
              <li>a memorable date, such as a birthday or anniversary</li>
              <li>any variation of the word ‘password’ </li>
              <li>the same password you use for other accounts</li>
              <li>any other single word, as these can be easy to guess</li>
            </ul>
          </div>
        </Section>

        <Section>
          <h2 className="ln-u-h4 ln-u-margin-bottom*1/2">
            How to choose a secure password
          </h2>
          <p>
            To choose a secure password we recommend creating something unique
            and memorable password by stringing together three or four random
            words.
          </p>
          <p>For example, you could choose:</p>
          <div className="ln-u-margin-bottom*2">
            <ul style={{ listStyle: "initial", marginBottom: "0" }}>
              <li>
                a <span className="ln-u-font-weight-bold">size</span> or{" "}
                <span className="ln-u-font-weight-bold">colour</span>
              </li>
              <li>
                an <span className="ln-u-font-weight-bold">action</span>
              </li>
              <li>
                an <span className="ln-u-font-weight-bold">object</span> or{" "}
                <span className="ln-u-font-weight-bold">animal</span>
              </li>
            </ul>
          </div>
          <p>To create something like:</p>
          <p className="ln-u-font-weight-bold">12LargeDancingDonkeys</p>
          <p>
            Combining this approach with a mix of uppercase letters and numbers
            means you’ll have a secure password that’s easy to remember.
          </p>
        </Section>

        <Section>
          <h2 className="ln-u-h4 ln-u-margin-bottom*1/2">Password tips</h2>
          <p>
            Once you’ve chosen a secure password, it’s important to make sure
            you:
          </p>
          <ul style={{ listStyle: "initial", marginBottom: "0" }}>
            <li>
              don’t share your password with anyone else (we’ll never ask for
              your password)
            </li>
            <li>choose a different password for every website you use</li>
            <li>change your password if you suspect somebody else knows it </li>
          </ul>
        </Section>
      </Card>
    </PageFormat>
  );
};

export default FindOutMorePage;
